import React from 'react';
import './EmptyState.css';

/**
 * Reusable empty state component with helpful guidance
 * @param {Object} props
 * @param {string} props.icon - PrimeIcons class (e.g., 'pi-inbox', 'pi-key')
 * @param {string} props.title - Main empty state message
 * @param {string} props.description - Helpful guidance text
 * @param {React.ReactNode} props.action - Optional action button or link
 * @param {string} props.variant - Style variant: 'default', 'subtle', 'card'
 */
export default function EmptyState({ 
  icon = 'pi-inbox', 
  title = 'No data found', 
  description = '',
  action = null,
  variant = 'default'
}) {
  return (
    <div className={`empty-state empty-state--${variant}`}>
      <div className="empty-state__icon">
        <i className={`pi ${icon}`} />
      </div>
      <h3 className="empty-state__title">{title}</h3>
      {description && (
        <p className="empty-state__description">{description}</p>
      )}
      {action && (
        <div className="empty-state__action">
          {action}
        </div>
      )}
    </div>
  );
}

/**
 * Pre-configured empty states for common scenarios
 */
export const EmptyStatePresets = {
  noLogs: (props = {}) => (
    <EmptyState
      icon="pi-file"
      title="No logs found"
      description="Try adjusting your date filters or send some logs via the SDK to get started."
      {...props}
    />
  ),
  
  noApiKeys: (props = {}) => (
    <EmptyState
      icon="pi-key"
      title="No API keys yet"
      description="Generate an API key to start sending logs from your application."
      {...props}
    />
  ),
  
  noMembers: (props = {}) => (
    <EmptyState
      icon="pi-users"
      title="No team members"
      description="You're the only member. Invite others to collaborate on this organization."
      {...props}
    />
  ),
  
  noInvites: (props = {}) => (
    <EmptyState
      icon="pi-envelope"
      title="No pending invites"
      description="Invite team members to join your organization by email."
      variant="subtle"
      {...props}
    />
  ),
  
  noRules: (props = {}) => (
    <EmptyState
      icon="pi-shield"
      title="No custom rules"
      description="Built-in rules are always active. Add custom patterns to redact additional sensitive data."
      {...props}
    />
  ),
  
  noBilling: (props = {}) => (
    <EmptyState
      icon="pi-credit-card"
      title="No billing information"
      description="Join or create an organization to view billing details."
      {...props}
    />
  ),
  
  error: (message = 'Something went wrong', props = {}) => (
    <EmptyState
      icon="pi-exclamation-triangle"
      title="Error"
      description={message}
      variant="subtle"
      {...props}
    />
  ),
  
  loading: (props = {}) => (
    <EmptyState
      icon="pi-spin pi-spinner"
      title="Loading..."
      variant="subtle"
      {...props}
    />
  )
};
